<template>
  <div class="history">
    <v-base-card>
      <template v-slot:heading>
        コンテンツ一覧
      </template>

      <template v-slot:content>
        <v-row class="mb-0 pb-0">
          <!-- 絞り込みは各列の見出しから行う（条件は別画面から戻っても残る） -->
          <v-col cols="10" class="d-flex align-center" style="gap: 12px">
            <span class="text-body-2">{{ filteredItems.length.toLocaleString() }}件 / 全{{ listItems.length.toLocaleString() }}件</span>
            <v-btn small outlined :disabled="!activeFilterCount && !sortBy.length" @click="clearAllFilters">
              <v-icon left small>mdi-filter-remove-outline</v-icon>フィルター・並び順をクリア
            </v-btn>
            <span class="text-caption grey--text">列の見出しをクリックすると、並べ替えと絞り込みができます</span>
          </v-col>
          <v-col cols="2" class="d-flex align-center" style="gap: 8px">
            <v-btn
              color="primary"
              small
              :loading="exporting"
              @click="exportXlsx"
            >
              XLSX出力
            </v-btn>
            <v-btn
              small
              color="#4a81aa"
              dark
              @click="showImportArea = !showImportArea"
            >
              取込
            </v-btn>
          </v-col>
        </v-row>
        <v-expand-transition>
          <v-row v-if="showImportArea" class="mx-1 mb-3" no-gutters>
            <!-- 左: XLSX属性取込 -->
            <v-col cols="6" class="pr-2">
              <div class="import-area">
                <div class="text-subtitle-2 mb-2">属性XLSX取込</div>
                <!-- 進捗表示 -->
                <div v-if="importJobs.length" class="mb-3">
                  <div
                    v-for="(job, idx) in importJobs"
                    :key="job.id"
                    class="import-job-item"
                    :class="{ 'mb-2': idx < importJobs.length - 1 }"
                  >
                    <div class="d-flex align-center mb-1">
                      <v-icon x-small class="mr-1" :color="jobStatusColor(job)">{{ jobStatusIcon(job) }}</v-icon>
                      <span class="text-caption font-weight-medium">{{ job.filename }}</span>
                      <span class="text-caption ml-2">{{ jobStatusText(job) }}</span>
                      <v-spacer></v-spacer>
                      <span class="text-caption text--secondary mr-1">
                        {{ job.success_count }}/{{ job.total_count }}
                        <span v-if="job.error_count > 0" class="red--text">({{ job.error_count }}件失敗)</span>
                      </span>
                      <v-btn
                        v-if="job.status === 'completed' || job.status === 'failed'"
                        icon
                        x-small
                        @click="removeImportJob(idx)"
                      ><v-icon x-small>mdi-close</v-icon></v-btn>
                    </div>
                    <v-progress-linear
                      :value="job.progress"
                      :color="jobStatusColor(job)"
                      height="16"
                      rounded
                    >
                      <template #default>
                        <span class="white--text" style="font-size: 10px">{{ job.progress }}%</span>
                      </template>
                    </v-progress-linear>
                    <div v-if="job.errors && job.errors.length" class="mt-1">
                      <v-alert type="warning" dense class="mb-0 text-caption pa-2">
                        <div v-for="(err, i) in job.errors" :key="i">{{ err }}</div>
                      </v-alert>
                    </div>
                  </div>
                </div>
                <!-- ドロップゾーン -->
                <div
                  class="dropzone-area"
                  :class="{ 'dropzone-active': dragging }"
                  @dragover.prevent="dragging = true"
                  @dragenter.prevent="dragging = true"
                  @dragleave.prevent="dragging = false"
                  @drop.prevent="onDrop"
                  @click="$refs.fileInput.click()"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    multiple
                    style="display: none"
                    @change="onFileSelect"
                  >
                  <div v-if="!importFiles.length" class="dropzone-placeholder">
                    <v-icon size="28" color="grey">mdi-file-upload-outline</v-icon>
                    <div class="text-body-2 grey--text mt-1">XLSXをドロップまたはクリック</div>
                  </div>
                  <div v-else>
                    <div
                      v-for="(file, i) in importFiles"
                      :key="i"
                      class="dropzone-selected d-flex align-center"
                      :class="{ 'mt-1': i > 0 }"
                    >
                      <v-icon small class="mr-2" color="#4a81aa">mdi-file-excel</v-icon>
                      <span class="text-body-2">{{ file.name }}</span>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small @click.stop="removeFile(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
                    </div>
                  </div>
                </div>
                <v-btn
                  small
                  color="#4a81aa"
                  dark
                  class="mt-2"
                  :loading="uploading"
                  :disabled="!importFiles.length"
                  @click="startImport"
                >
                  取込開始（{{ importFiles.length }}ファイル）
                </v-btn>
              </div>
            </v-col>
            <!-- 右: 画像/ファイルZIP取込 -->
            <v-col cols="6" class="pl-2">
              <div class="import-area">
                <div class="text-subtitle-2 mb-2">画像/ファイルZIP取込</div>
                <!-- 進捗表示 -->
                <div v-if="imageImportJobs.length" class="mb-3">
                  <div
                    v-for="(job, idx) in imageImportJobs"
                    :key="job.id"
                    class="import-job-item"
                    :class="{ 'mb-2': idx < imageImportJobs.length - 1 }"
                  >
                    <div class="d-flex align-center mb-1">
                      <v-icon x-small class="mr-1" :color="jobStatusColor(job)">{{ jobStatusIcon(job) }}</v-icon>
                      <span class="text-caption font-weight-medium">{{ job.filename }}</span>
                      <span class="text-caption ml-2">{{ jobStatusText(job) }}</span>
                      <v-spacer></v-spacer>
                      <span class="text-caption text--secondary mr-1">
                        {{ job.success_count }}/{{ job.total_count }}
                        <span v-if="job.error_count > 0" class="red--text">({{ job.error_count }}件失敗)</span>
                      </span>
                      <v-btn
                        v-if="job.status === 'completed' || job.status === 'failed'"
                        icon
                        x-small
                        @click="removeImageImportJob(idx)"
                      ><v-icon x-small>mdi-close</v-icon></v-btn>
                    </div>
                    <v-progress-linear
                      :value="job.progress"
                      :color="jobStatusColor(job)"
                      height="16"
                      rounded
                    >
                      <template #default>
                        <span class="white--text" style="font-size: 10px">{{ job.progress }}%</span>
                      </template>
                    </v-progress-linear>
                    <div v-if="job.errors && job.errors.length" class="mt-1">
                      <v-alert type="warning" dense class="mb-0 text-caption pa-2">
                        <div v-for="(err, i) in job.errors" :key="i">{{ err }}</div>
                      </v-alert>
                    </div>
                  </div>
                </div>
                <!-- ドロップゾーン -->
                <div
                  class="dropzone-area"
                  :class="{ 'dropzone-active': imageDragging }"
                  @dragover.prevent="imageDragging = true"
                  @dragenter.prevent="imageDragging = true"
                  @dragleave.prevent="imageDragging = false"
                  @drop.prevent="onImageDrop"
                  @click="$refs.imageFileInput.click()"
                >
                  <input
                    ref="imageFileInput"
                    type="file"
                    accept=".zip"
                    multiple
                    style="display: none"
                    @change="onImageFileSelect"
                  >
                  <div v-if="!imageImportFiles.length" class="dropzone-placeholder">
                    <v-icon size="28" color="grey">mdi-folder-zip-outline</v-icon>
                    <div class="text-body-2 grey--text mt-1">ZIPをドロップまたはクリック</div>
                  </div>
                  <div v-else>
                    <div
                      v-for="(file, i) in imageImportFiles"
                      :key="i"
                      class="dropzone-selected d-flex align-center"
                      :class="{ 'mt-1': i > 0 }"
                    >
                      <v-icon small class="mr-2" color="#6a9b5e">mdi-folder-zip</v-icon>
                      <span class="text-body-2">{{ file.name }}</span>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small @click.stop="removeImageFile(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
                    </div>
                  </div>
                </div>
                <v-btn
                  small
                  color="#6a9b5e"
                  dark
                  class="mt-2"
                  :loading="imageUploading"
                  :disabled="!imageImportFiles.length"
                  @click="startImageImport"
                >
                  取込開始（{{ imageImportFiles.length }}ファイル）
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="loading"
          loading-text="Loading... Please wait"
          fixed-header
          :height="'calc(100vh - 300px)'"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="tablePage"
          :items-per-page.sync="itemsPerPage"
          :footer-props="{ 'items-per-page-options': [50, 100, 200, -1] }"
        >
          <!-- 列の見出し: Excelのオートフィルターのように並べ替え・絞り込みができる -->
          <template v-for="column in filterColumns" v-slot:[`header.${column.value}`]>
            <v-column-filter-menu
              :key="column.value"
              :column="column"
              :items="listItems"
              :filter="columnFilters[column.value]"
              :sort-direction="columnSortDirection(column.value)"
              @filter="setColumnFilter(column.value, $event)"
              @sort="setColumnSort(column.value, $event)"
            />
          </template>
          <template #[`item.content_type`]="{ item }">
            {{ item.content_type === 'free' ? '無料' : '有料' }}
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at) }}
          </template>
          <template #[`item.edit`]="{ item }">
            <v-btn icon @click.stop="edit(item)">
              <v-icon small dark>mdi-cog-outline</v-icon>
            </v-btn>
          </template>
          <template #[`item.delete`]="{ item }">
            <v-btn icon @click.stop="openDeleteConfirmDialog(item)">
              <v-icon dark>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </v-base-card>

    <v-confirm-dialog
      ref="confirm"
      title="コンテンツ削除"
      :message="deleteMessage"
      buttonMessage="削除"
      @confirm="confirmDeletion"
    />
  </div>
</template>

<script>
import VBaseCard from '@/components/molecules/BaseCard'
import VConfirmDialog from '@/components/atoms/ConfirmDialog'
import VColumnFilterMenu from '@/components/molecules/ColumnFilterMenu'
import columnFilterList from '@/mixins/columnFilterList'

export default {
  components: {
    VBaseCard,
    VConfirmDialog,
    VColumnFilterMenu,
  },
  mixins: [columnFilterList('admin-contents-list')],
  data () {
    return {
      // 並べ替えは列見出しのメニューから行うため、見出しクリックでの並べ替えは無効にする
      headers: [
        { text: 'コード', align: 'start', value: 'code', sortable: false },
        { text: '単元名', align: 'start', value: 'name', sortable: false },
        { text: '教科', align: 'center', value: 'subject_name', sortable: false },
        { text: '学年', align: 'center', value: 'grade_name', sortable: false },
        { text: '難易度', align: 'center', value: 'difficulty_name', sortable: false },
        { text: '種別', align: 'center', value: 'content_type', sortable: false },
        { text: '年度', align: 'center', value: 'fiscal_year', sortable: false },
        { text: '設問数', align: 'center', value: 'question_cnt', sortable: false },
        { text: '登録日', align: 'center', value: 'created_at', sortable: false },
        { text: '更新日', align: 'center', value: 'updated_at', sortable: false },
        { text: '編集', align: 'center', value: 'edit', sortable: false },
        { text: '削除', align: 'center', value: 'delete', sortable: false },
      ],
      deletedContent: { id: null, name: '' },
      loading: false,
      exporting: false,
      showImportArea: false,
      importFiles: [],
      uploading: false,
      dragging: false,
      importJobs: [],
      pollTimer: null,
      imageImportFiles: [],
      imageUploading: false,
      imageDragging: false,
      imageImportJobs: [],
      imagePollTimer: null,
    }
  },

  async mounted() {
    await this.getContents()
    this.restoreImportJobs()
    this.restoreImageImportJobs()
  },

  beforeDestroy() {
    this.stopPolling()
    this.stopImagePolling()
  },

  computed: {
    deleteMessage() {
      return `${this.deletedContent.name} (${this.deletedContent.code}) を削除します。よろしいですか。`
    },
    // 列ごとの絞り込み方（select: 値のチェックリスト / text: 文字を含む / date: 期間）
    filterColumns() {
      const column = (value, type, label) => ({ ...this.headers.find(h => h.value === value), type, label })
      return [
        column('code', 'text', item => item.code),
        column('name', 'text', item => item.name),
        column('subject_name', 'select', item => item.subject_name),
        column('grade_name', 'select', item => item.grade_name),
        column('difficulty_name', 'select', item => item.difficulty_name),
        column('content_type', 'select', item => (item.content_type === 'free' ? '無料' : '有料')),
        column('fiscal_year', 'select', item => item.fiscal_year),
        column('question_cnt', 'select', item => item.question_cnt),
        column('created_at', 'date', item => item.created_at),
        column('updated_at', 'date', item => item.updated_at),
      ]
    },
    hasRunningJobs() {
      return this.importJobs.some(j => j.status === 'pending' || j.status === 'processing')
    },
    hasRunningImageJobs() {
      return this.imageImportJobs.some(j => j.status === 'pending' || j.status === 'processing')
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      return dateStr.slice(0, 10)
    },
    jobStatusColor(job) {
      if (job.status === 'completed') return job.error_count > 0 ? 'orange' : 'green'
      if (job.status === 'failed') return 'red'
      return 'blue'
    },
    jobStatusIcon(job) {
      if (job.status === 'completed') return job.error_count > 0 ? 'mdi-alert-circle' : 'mdi-check-circle'
      if (job.status === 'failed') return 'mdi-close-circle'
      return 'mdi-progress-clock'
    },
    jobStatusText(job) {
      if (job.status === 'pending') return '準備中...'
      if (job.status === 'processing') return '取り込み中...'
      if (job.status === 'completed') return job.error_count > 0 ? '完了（一部エラー）' : '完了'
      if (job.status === 'failed') return '失敗'
      return ''
    },
    async getContents() {
      this.loading = true
      const res = await this.$axios('/api/v1/admin/contents?all=1')
      this.listItems = res.data.items
      this.loading = false
      this.applyRestoredPage()
    },
    openDeleteConfirmDialog(content) {
      this.deletedContent = content
      this.$refs.confirm.open()
    },
    async confirmDeletion() {
      const url = `api/v1/admin/contents/${this.deletedContent.id}`
      await this.$axios.delete(url)
      this.$toasted.global.success({ message: `${this.deletedContent.name}を削除しました` })
      this.getContents()
    },
    async exportXlsx() {
      this.exporting = true
      try {
        // 絞り込み中は表示中の行だけを出力する（IDが多くなるためPOSTで送る）
        const res = this.activeFilterCount
          ? await this.$axios.post('/api/v1/admin/contents/export', { ids: this.filteredItems.map(item => item.id) }, { responseType: 'blob' })
          : await this.$axios('/api/v1/admin/contents/export', { responseType: 'blob' })
        const contentDisposition = res.headers['content-disposition']
        let filename = '属性情報.xlsx'
        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;\n]+)/i)
          if (match) filename = decodeURIComponent(match[1].replace(/["']/g, ''))
        }
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$toasted.global.error({ message: 'エクスポートに失敗しました' })
      } finally {
        this.exporting = false
      }
    },
    edit(content) {
      this.$router.push(`/admin/content/${content.id}`)
    },
    onDrop(e) {
      this.dragging = false
      const files = Array.from(e.dataTransfer.files).filter(f => /\.xlsx?$/i.test(f.name))
      if (files.length === 0) {
        this.$toasted.global.error({ message: 'XLSXファイルを選択してください' })
        return
      }
      this.importFiles = [...this.importFiles, ...files]
    },
    onFileSelect(e) {
      const files = Array.from(e.target.files).filter(f => /\.xlsx?$/i.test(f.name))
      this.importFiles = [...this.importFiles, ...files]
      e.target.value = ''
    },
    removeFile(index) {
      this.importFiles.splice(index, 1)
    },
    async startImport() {
      if (!this.importFiles.length) return
      this.uploading = true
      try {
        for (const file of this.importFiles) {
          const formData = new FormData()
          formData.append('file', file)
          const res = await this.$axios.post('/api/v1/admin/contents/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          this.importJobs.push({
            id: res.data.import_job_id,
            filename: file.name,
            status: 'pending',
            total_count: 0,
            success_count: 0,
            error_count: 0,
            progress: 0,
            errors: [],
          })
        }
        this.importFiles = []
        this.saveImportJobs()
        this.$toasted.global.success({ message: 'バックグラウンドで取り込みを開始しました' })
        this.startPolling()
      } catch (e) {
        this.$toasted.global.error({ message: 'ファイルのアップロードに失敗しました' })
      } finally {
        this.uploading = false
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollImportStatus()
      this.pollTimer = setInterval(() => {
        this.pollImportStatus()
      }, 2000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async pollImportStatus() {
      const running = this.importJobs.filter(j => j.status === 'pending' || j.status === 'processing')
      if (!running.length) {
        this.stopPolling()
        return
      }
      for (const job of running) {
        try {
          const res = await this.$axios(`/api/v1/admin/contents/import_status/${job.id}`)
          Object.assign(job, res.data)
          if (res.data.status === 'completed') {
            this.getContents()
          }
        } catch (e) {
          // ignore
        }
      }
      this.saveImportJobs()
      // 全部終わったらポーリング停止
      if (!this.hasRunningJobs) {
        this.stopPolling()
      }
    },
    saveImportJobs() {
      const data = this.importJobs.map(j => ({ id: j.id, filename: j.filename }))
      localStorage.setItem('contentImportJobs', JSON.stringify(data))
    },
    restoreImportJobs() {
      try {
        const saved = JSON.parse(localStorage.getItem('contentImportJobs') || '[]')
        if (!saved.length) return
        this.importJobs = saved.map(j => ({
          id: j.id,
          filename: j.filename,
          status: 'pending',
          total_count: 0,
          success_count: 0,
          error_count: 0,
          progress: 0,
          errors: [],
        }))
        this.showImportArea = true
        this.startPolling()
      } catch (e) {
        localStorage.removeItem('contentImportJobs')
      }
    },
    removeImportJob(index) {
      this.importJobs.splice(index, 1)
      this.saveImportJobs()
    },
    onImageDrop(e) {
      this.imageDragging = false
      const files = Array.from(e.dataTransfer.files).filter(f => /\.zip$/i.test(f.name))
      if (files.length === 0) {
        this.$toasted.global.error({ message: 'ZIPファイルを選択してください' })
        return
      }
      this.imageImportFiles = [...this.imageImportFiles, ...files]
    },
    onImageFileSelect(e) {
      const files = Array.from(e.target.files).filter(f => /\.zip$/i.test(f.name))
      this.imageImportFiles = [...this.imageImportFiles, ...files]
      e.target.value = ''
    },
    removeImageFile(index) {
      this.imageImportFiles.splice(index, 1)
    },
    async startImageImport() {
      if (!this.imageImportFiles.length) return
      this.imageUploading = true
      try {
        for (const file of this.imageImportFiles) {
          const formData = new FormData()
          formData.append('file', file)
          const res = await this.$axios.post('/api/v1/admin/contents/import_images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          this.imageImportJobs.push({
            id: res.data.import_job_id,
            filename: file.name,
            status: 'pending',
            total_count: 0,
            success_count: 0,
            error_count: 0,
            progress: 0,
            errors: [],
          })
        }
        this.imageImportFiles = []
        this.saveImageImportJobs()
        this.$toasted.global.success({ message: '画像取り込みをバックグラウンドで開始しました' })
        this.startImagePolling()
      } catch (e) {
        this.$toasted.global.error({ message: 'ZIPファイルのアップロードに失敗しました' })
      } finally {
        this.imageUploading = false
      }
    },
    startImagePolling() {
      this.stopImagePolling()
      this.pollImageImportStatus()
      this.imagePollTimer = setInterval(() => {
        this.pollImageImportStatus()
      }, 2000)
    },
    stopImagePolling() {
      if (this.imagePollTimer) {
        clearInterval(this.imagePollTimer)
        this.imagePollTimer = null
      }
    },
    async pollImageImportStatus() {
      const running = this.imageImportJobs.filter(j => j.status === 'pending' || j.status === 'processing')
      if (!running.length) {
        this.stopImagePolling()
        return
      }
      for (const job of running) {
        try {
          const res = await this.$axios(`/api/v1/admin/contents/import_status/${job.id}`)
          Object.assign(job, res.data)
        } catch (e) {
          // ignore
        }
      }
      this.saveImageImportJobs()
      if (!this.hasRunningImageJobs) {
        this.stopImagePolling()
      }
    },
    saveImageImportJobs() {
      const data = this.imageImportJobs.map(j => ({ id: j.id, filename: j.filename }))
      localStorage.setItem('contentImageImportJobs', JSON.stringify(data))
    },
    restoreImageImportJobs() {
      try {
        const saved = JSON.parse(localStorage.getItem('contentImageImportJobs') || '[]')
        if (!saved.length) return
        this.imageImportJobs = saved.map(j => ({
          id: j.id,
          filename: j.filename,
          status: 'pending',
          total_count: 0,
          success_count: 0,
          error_count: 0,
          progress: 0,
          errors: [],
        }))
        this.showImportArea = true
        this.startImagePolling()
      } catch (e) {
        localStorage.removeItem('contentImageImportJobs')
      }
    },
    removeImageImportJob(index) {
      this.imageImportJobs.splice(index, 1)
      this.saveImageImportJobs()
    },
  }
}
</script>

<style lang="stylus" scoped>
.select
  font-size 11px !important

.import-area
  padding 12px
  background #fafafa
  border 1px solid #e0e0e0
  border-radius 4px

.dropzone-area
  border 2px dashed #bdbdbd
  border-radius 6px
  padding 20px
  text-align center
  cursor pointer
  transition border-color 0.2s, background 0.2s

  &:hover
    border-color #4a81aa
    background #f0f6fb

  &.dropzone-active
    border-color #4a81aa
    background #e3f0fa

.dropzone-selected
  text-align left

.import-job-item
  padding 8px
  background #fff
  border 1px solid #e8e8e8
  border-radius 4px
</style>
